// App.jsx
import { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon, Download, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
// import { useToast } from "@/components/ui/use-toast";

const ContactsDashboard = () => {
  // const { toast } = useToast();
  
  // State management
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("phoneNumber");
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [dateType, setDateType] = useState("range");
  const [singleDate, setSingleDate] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null,
  });
  const [analytics, setAnalytics] = useState({
    totalContacts: 0,
    incomingContacts: 0,
    outgoingContacts: 0,
    conversionRate: 0,
    averageConversionTimeDays: 0,
    agentConversionTimes: {}
  });
  
  // Fetch contacts based on filters
  const fetchContacts = async () => {
    setLoading(true);
    try {
      let url = new URL('https://pukfh1sc9a.execute-api.ap-south-1.amazonaws.com/prod/contacts', window.location.origin);
      
      // Add search query if provided
      if (search) {
        url.searchParams.append(searchField, search);
      }
      
      // Add agent filter if selected
      if (selectedAgent) {
        url.searchParams.append('agent', selectedAgent);
      }
      
      // Add course filter if selected
      if (selectedCourse) {
        url.searchParams.append('courseName', selectedCourse);
      }
      
      // Add date filters
      if (dateType === 'single' && singleDate) {
        const formattedDate = format(singleDate, 'yyyy-MM-dd');
        url.searchParams.append('startDate', formattedDate);
        url.searchParams.append('endDate', formattedDate);
      } else if (dateType === 'range' && dateRange.from) {
        const formattedStartDate = format(dateRange.from, 'yyyy-MM-dd');
        url.searchParams.append('startDate', formattedStartDate);
        
        if (dateRange.to) {
          const formattedEndDate = format(dateRange.to, 'yyyy-MM-dd');
          url.searchParams.append('endDate', formattedEndDate);
        }
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setContacts(data.data);
        calculateAnalytics(data.data);
      } else {
        // toast({
        //   title: "Error",
        //   description: data.error || "Failed to fetch contacts",
        //   variant: "destructive",
        // });
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
      // toast({
      //   title: "Error",
      //   description: "Failed to fetch contacts. Please try again.",
      //   variant: "destructive",
      // });
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate analytics from contact data
  const calculateAnalytics = (contactsData) => {
    const totalContacts = contactsData.length;
    const incomingContacts = contactsData.filter(contact => contact.category === "Incoming").length;
    const outgoingContacts = contactsData.filter(contact => contact.category === "Outgoing").length;
    
    // Calculate conversion times per agent
    const agentConversionTimes = {};
    const agentsWithConversionTimes = contactsData.filter(contact => contact.conversionTimeDays);
    
    agentsWithConversionTimes.forEach(contact => {
      const agent = contact.owner || "Unknown";
      
      if (!agentConversionTimes[agent]) {
        agentConversionTimes[agent] = {
          totalDays: 0,
          count: 0
        };
      }
      
      agentConversionTimes[agent].totalDays += parseFloat(contact.conversionTimeDays);
      agentConversionTimes[agent].count += 1;
    });
    
    // Calculate average conversion time
    let totalConversionDays = 0;
    let contactsWithConversionTime = 0;
    
    Object.keys(agentConversionTimes).forEach(agent => {
      const { totalDays, count } = agentConversionTimes[agent];
      agentConversionTimes[agent].average = (totalDays / count).toFixed(2);
      
      totalConversionDays += totalDays;
      contactsWithConversionTime += count;
    });
    
    const averageConversionTimeDays = contactsWithConversionTime > 0 
      ? (totalConversionDays / contactsWithConversionTime).toFixed(2)
      : 0;
    
    setAnalytics({
      totalContacts,
      incomingContacts,
      outgoingContacts,
      conversionRate: totalContacts > 0 ? ((incomingContacts / totalContacts) * 100).toFixed(2) : 0,
      averageConversionTimeDays,
      agentConversionTimes
    });
  };
  
  // Fetch agents data
  const fetchAgents = async () => {
    try {
      const response = await fetch('https://pukfh1sc9a.execute-api.ap-south-1.amazonaws.com/prod/agents/agents');
      const data = await response.json();
      
      if (data.agents) {
        setAgents(data.agents);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };
  
  // Fetch courses data
  const fetchCourses = async () => {
    try {
      // Placeholder - replace with actual API call
      setCourses([
        { id: 1, name: "Live Prelims Revision Program Batch 2" },
        { id: 2, name: "UPSC Foundation Course" },
        { id: 3, name: "Mains Answer Writing Program" }
      ]);
    } catch (error) {
      console.error("Error fetching courses:", error);
    }
  };
  
  // Export contacts to CSV
  const exportToCSV = async () => {
    try {
      // Construct the same URL with filters as for fetching
      let url = new URL('https://pukfh1sc9a.execute-api.ap-south-1.amazonaws.com/prod/contacts/export', window.location.origin);
      
      if (search) {
        url.searchParams.append(searchField, search);
      }
      
      if (selectedAgent) {
        url.searchParams.append('agent', selectedAgent);
      }
      
      if (selectedCourse) {
        url.searchParams.append('courseName', selectedCourse);
      }
      
      if (dateType === 'single' && singleDate) {
        const formattedDate = format(singleDate, 'yyyy-MM-dd');
        url.searchParams.append('startDate', formattedDate);
        url.searchParams.append('endDate', formattedDate);
      } else if (dateType === 'range' && dateRange.from) {
        const formattedStartDate = format(dateRange.from, 'yyyy-MM-dd');
        url.searchParams.append('startDate', formattedStartDate);
        
        if (dateRange.to) {
          const formattedEndDate = format(dateRange.to, 'yyyy-MM-dd');
          url.searchParams.append('endDate', formattedEndDate);
        }
      }
      
      // Fetch the CSV file
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Create download link
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `contacts-export-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      
      // Trigger the download
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // toast({
      //   title: "Export Successful",
      //   description: "Contacts data has been exported to CSV.",
      // });
    } catch (error) {
      console.error("Error exporting contacts:", error);
      // toast({
      //   title: "Export Failed",
      //   description: "Failed to export contacts data.",
      //   variant: "destructive",
      // });
    }
  };
  
  // Load data on initial mount
  useEffect(() => {
    fetchAgents();
    fetchCourses();
    fetchContacts();
  }, []);
  
  // Reset filters
  const resetFilters = () => {
    setSearch("");
    setSearchField("phoneNumber");
    setSelectedAgent("");
    setSelectedCourse("");
    setDateType("range");
    setSingleDate(null);
    setDateRange({
      from: null,
      to: null,
    });
  };
  
  // Apply filters
  const applyFilters = () => {
    fetchContacts();
  };
  
  return (
    <div className="container mx-auto py-6 px-4 md:px-6">
      <h1 className="text-3xl font-bold mb-6">Contacts Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Total Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{analytics.totalContacts}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Incoming Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{analytics.incomingContacts}</p>
            <p className="text-sm text-muted-foreground">
              {analytics.conversionRate}% of total
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Outgoing Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{analytics.outgoingContacts}</p>
            <p className="text-sm text-muted-foreground">
              {analytics.outgoingContacts > 0 
                ? ((analytics.outgoingContacts / analytics.totalContacts) * 100).toFixed(2) 
                : 0}% of total
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Avg. Conversion Time</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{analytics.averageConversionTimeDays}</p>
            <p className="text-sm text-muted-foreground">days</p>
          </CardContent>
        </Card>
      </div>
      
      <Tabs defaultValue="contacts" className="mb-6">
        <TabsList>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="conversionAnalytics">Conversion Analytics</TabsTrigger>
        </TabsList>
        
        <TabsContent value="contacts">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Filter Contacts</CardTitle>
              <CardDescription>
                Search and filter contacts based on various parameters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="space-y-2">
                  <label htmlFor="search" className="text-sm font-medium">
                    Search
                  </label>
                  <div className="flex gap-2">
                    <Select value={searchField} onValueChange={setSearchField}>
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Search by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phoneNumber">Phone</SelectItem>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex-1 relative">
                      <Input
                        id="search"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pr-8"
                      />
                      <Search className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Agent
                  </label>
                  <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Agents</SelectItem>
                      {agents.map((agent) => (
                        <SelectItem key={agent.username} value={agent.username}>
                          {agent.fullName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Course
                  </label>
                  <Select value={selectedCourse} onValueChange={setSelectedCourse}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select course" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Courses</SelectItem>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.name}>
                          {course.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">
                      Date Filter
                    </label>
                    <Select 
                      value={dateType} 
                      onValueChange={setDateType}
                      className="w-28"
                    >
                      <SelectTrigger className="h-7">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="range">Range</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {dateType === 'single' ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {singleDate ? format(singleDate, 'PPP') : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={singleDate}
                          onSelect={setSingleDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateRange.from ? (
                            dateRange.to ? (
                              `${format(dateRange.from, 'PP')} - ${format(dateRange.to, 'PP')}`
                            ) : (
                              format(dateRange.from, 'PP')
                            )
                          ) : (
                            "Select date range"
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="range"
                          selected={dateRange}
                          onSelect={setDateRange}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={resetFilters}>
                  <X className="mr-2 h-4 w-4" />
                  Reset
                </Button>
                <Button onClick={applyFilters}>
                  <Search className="mr-2 h-4 w-4" />
                  Apply Filters
                </Button>
                <Button variant="secondary" onClick={exportToCSV}>
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Contacts</CardTitle>
              <CardDescription>
                {contacts.length} contacts found
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Course</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Conv. Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      Array(5).fill().map((_, i) => (
                        <TableRow key={i}>
                          {Array(8).fill().map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : contacts.length > 0 ? (
                      contacts.map((contact, index) => (
                        <TableRow key={index}>
                          <TableCell>{contact.name || "N/A"}</TableCell>
                          <TableCell>{contact.phoneNumber}</TableCell>
                          <TableCell>{contact.email || "N/A"}</TableCell>
                          <TableCell>{contact.date}</TableCell>
                          <TableCell>{contact.eCourse || contact.courseName || "N/A"}</TableCell>
                          <TableCell>{contact.owner || "N/A"}</TableCell>
                          <TableCell>
                            <Badge variant={contact.category === "Incoming" ? "default" : "secondary"}>
                              {contact.category || "N/A"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {contact.conversionTimeDays ? `${contact.conversionTimeDays} days` : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center h-24">
                          No contacts found. Try adjusting your filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              
              {contacts.length > 0 && !loading && (
                <div className="flex justify-center mt-4">
                  <Button variant="outline">Load More</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="conversionAnalytics">
          <Card>
            <CardHeader>
              <CardTitle>Conversion Time by Agent</CardTitle>
              <CardDescription>
                Average days from lead creation to conversion
              </CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(analytics.agentConversionTimes).length > 0 ? (
                <div className="space-y-4">
                  {Object.keys(analytics.agentConversionTimes).map((agent) => {
                    const data = analytics.agentConversionTimes[agent];
                    return (
                      <div key={agent} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{agent}</p>
                          <p className="text-sm text-muted-foreground">
                            {data.count} contacts
                          </p>
                        </div>
                        <div className="flex items-center">
                          <div className="w-40 bg-gray-100 rounded-full h-2.5 mr-2">
                            <div 
                              className="bg-primary h-2.5 rounded-full" 
                              style={{ 
                                width: `${Math.min(100, (data.average / 30) * 100)}%`
                              }}
                            />
                          </div>
                          <p className="font-medium">{data.average} days</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    No conversion data available. Try adjusting your filters.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContactsDashboard;